<template>
    <app-dialog :value="value" size="xl" @input="$emit('input', $event)">
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                Comparativa de planes
            </h2>
            <p class="mt-1 text-sm text-theme-muted">
                Todo lo que incluye cada plan, fila por fila.
            </p>
        </template>

        <div class="overflow-x-auto">
            <div class="grid grid-cols-plan-compare items-end gap-2 pb-2">
                <div></div>
                <div
                    v-for="plan in orderedPlans"
                    :key="plan.code"
                    class="rounded-lg px-2 py-2 text-center"
                    :class="
                        hasTrial(plan)
                            ? 'border-2 border-theme-primary'
                            : 'border border-theme-border'
                    "
                >
                    <div
                        class="text-2xs font-semibold uppercase tracking-wide text-theme-muted"
                    >
                        {{ plan.name }}
                    </div>
                    <div
                        class="text-base font-bold text-theme-base whitespace-nowrap"
                    >
                        {{ planPrice(plan) }}
                    </div>
                    <div v-if="plan.price" class="text-2xs text-theme-muted">
                        {{ periodSuffix(plan) }}
                    </div>
                    <div
                        v-if="plan.period && plan.period !== 'monthly'"
                        class="text-2xs text-theme-muted"
                    >
                        {{ monthlyPrice(plan) }}/mes
                    </div>
                    <div
                        v-if="savings(plan) > 0"
                        class="text-2xs font-bold text-theme-success"
                    >
                        Ahorras {{ savings(plan) }} %
                    </div>
                </div>
            </div>

            <div
                v-for="(row, i) in rows"
                :key="row.feature"
                class="grid grid-cols-plan-compare gap-2"
                :class="i % 2 === 0 ? 'bg-theme-card' : 'bg-theme-canvas'"
            >
                <div
                    class="flex items-center px-2 py-2 text-xs font-medium text-theme-muted"
                    :class="i > 0 ? 'border-t border-theme-border' : ''"
                >
                    {{ row.feature }}
                </div>
                <div
                    v-for="(val, idx) in row.values"
                    :key="idx"
                    class="flex items-center justify-center px-2 py-2 text-xs"
                    :class="[
                        i > 0 ? 'border-t border-theme-border' : '',
                        orderedPlans[idx] && hasTrial(orderedPlans[idx])
                            ? 'bg-theme-selected'
                            : ''
                    ]"
                >
                    <span v-if="val === true" class="text-theme-success"
                        >✓</span
                    >
                    <span v-else-if="val === false" class="text-theme-muted"
                        >✕</span
                    >
                    <span v-else class="text-theme-base">{{ val }}</span>
                </div>
            </div>
        </div>
    </app-dialog>
</template>

<script>
import { currencyFilter } from "@/common/lib/filters";
import {
    PLAN_PERIOD_SUFFIXES,
    monthlyEquivalent,
    savingsPercent
} from "@/common/lib/plan-periods";

import AppDialog from "@/components/utils/AppDialog.vue";

const PLAN_CODE_ORDER = ["free", "starter", "pro", "premium"];

// Mismo contenido que la tabla comparativa de planes.vue en el landing
// (repos/landing/app/pages/planes.vue) — no viene del backend porque la
// mayoria de estas filas no son entitlements, son features fijas.
const ROWS = [
    { feature: "Foto de perfil y bio", values: [true, true, true, true] },
    {
        feature: "Colores y tema personalizado",
        values: [true, true, true, true]
    },
    { feature: "Sin marca de agua", values: [false, true, true, true] },
    {
        feature: "Redes sociales",
        values: ["Hasta 4", "Sin límite", "Sin límite", "Sin límite"]
    },
    {
        feature: "Enlaces personalizados",
        values: ["Hasta 5", "Hasta 20", "Sin límite", "Sin límite"]
    },
    {
        feature: "URL personalizada",
        values: ["Parcial", "Parcial", "Sí", "Sí"]
    },
    { feature: "Dominio personalizado", values: [false, false, false, true] },
    {
        feature: "Almacenamiento de fotos",
        values: ["100 MB", "500 MB", "1 GB", "2 GB"]
    },
    {
        feature: "Productos",
        values: [false, "Hasta 20", "Hasta 120", "Sin límite"]
    },
    {
        feature: "Órdenes internas",
        values: [
            false,
            "Hasta 50 / mes",
            "Hasta 500 / mes",
            "Hasta 2.000 / mes"
        ]
    },
    {
        feature: "Categorías y subcategorías",
        values: [false, false, true, true]
    },
    { feature: "Usuarios", values: ["1", "1", "3", "5"] },
    { feature: "Sucursales", values: [false, false, false, "Hasta 3"] },
    {
        feature: "Inventario",
        values: [false, false, "Global", "Global o por sucursal"]
    },
    {
        feature: "Recibir órdenes por el catálogo",
        values: [false, false, true, true]
    },
    { feature: "Métodos de pago", values: [false, false, true, true] },
    {
        feature: "Notificaciones de órdenes",
        values: [
            false,
            false,
            "Correo · Telegram",
            "Correo · Telegram · WhatsApp · Slack"
        ]
    },
    {
        feature: "Bloques en el editor",
        values: [false, "Hasta 10", "Hasta 20", "Hasta 50"]
    },
    {
        feature: "Páginas personalizadas",
        values: [false, "Hasta 5", "Hasta 20", "Hasta 100"]
    },
    {
        feature: "Conexión con Claude | ChatGPT",
        values: [false, false, "Próximamente", "Próximamente"]
    },
    {
        feature: "API para automatizaciones",
        values: [false, false, "Próximamente", "Próximamente"]
    }
];

export default {
    name: "PlanComparisonModal",

    components: { AppDialog },

    props: {
        value: {
            type: Boolean,
            required: true
        },
        plans: {
            type: Array,
            default: () => []
        },
        // Fila mensual de cada plan, por `code`: referencia para el ahorro
        // cuando `plans` trae filas trimestrales o anuales.
        monthlyPlans: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return { rows: ROWS };
    },

    computed: {
        orderedPlans() {
            return PLAN_CODE_ORDER.map((code) =>
                this.plans.find((plan) => plan.code === code)
            ).filter(Boolean);
        }
    },

    methods: {
        planPrice(plan) {
            if (!plan.price) return "Gratis";
            return currencyFilter(plan.price);
        },
        periodSuffix(plan) {
            return (
                PLAN_PERIOD_SUFFIXES[plan.period] ||
                PLAN_PERIOD_SUFFIXES.monthly
            );
        },
        monthlyPrice(plan) {
            return currencyFilter(monthlyEquivalent(plan));
        },
        hasTrial(plan) {
            const monthly = this.monthlyPlans[plan.code];
            return Boolean(
                plan.trialAvailable || (monthly && monthly.trialAvailable)
            );
        },
        savings(plan) {
            return savingsPercent(plan, this.monthlyPlans[plan.code]);
        }
    }
};
</script>
